import { useMemo, useState } from 'react';
import { Box, Typography, IconButton, List, ListItem, ListItemButton, TextField, InputAdornment } from '@mui/material';
import { Search, Close, Check } from '@mui/icons-material';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { Customer, StyleObj } from '../../@types';
import { SelectableUserRole, USER_ROLES, UserRole } from '../../constants/roles';
import useLocalization from '../../hooks/useLocalization';
import { useModal } from '../../hooks/useDialog';
import { mergeSx } from '../../utils/styles';
import useDebounce from '../../hooks/useDebounce';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import { QUERY_KEYS } from '../../constants';
import { LoaderContainer } from '../atoms/LoaderContainer';
import InfiniteScrollLoader from '../atoms/InfiniteScrollLoader';
import { useRoles } from '../../hooks/useRoles';

const styles: StyleObj = {
  modalContent: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    backgroundColor: 'neutral.50',
    height: '60vh',
    maxHeight: 600,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: 2,
  },
  searchTextField: {
    px: 2,
    pb: 1,
  },
  searchInput: {
    color: 'neutral.800',
    backgroundColor: 'neutral.100',
    px: 1,
    borderRadius: 1,
    width: '100%',
    '& .MuiInputBase-input': {
      p: 1.5,
      pl: 1,
    },
  },
  userList: {
    flex: 1,
    overflow: 'auto',
    p: 0,
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'neutral.50',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'neutral.300',
    },
  },
  userButton: {
    width: '100%',
    justifyContent: 'space-between',
    py: 1.5,
    px: 2,
    '&:hover': {
      backgroundColor: 'neutral.25',
    },
  },
  selectedUserButton: {
    backgroundColor: 'primary.50',
    '&:hover': {
      backgroundColor: 'primary.100',
    },
  },
  checkIcon: {
    color: 'primary.main',
    fontSize: 20,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    py: 4,
    color: 'neutral.400',
    textAlign: 'center',
  },
};

type SelectedUser = {
  id: string;
  username: string;
};

type UserSelectionModalProps = {
  selectedUserId?: string;
  onUserSelect: (user: SelectedUser | null) => void;
  userType: SelectableUserRole;
  title?: string;
};

const UserSelectionModal = ({ selectedUserId, onUserSelect, userType, title }: UserSelectionModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const { isPlayer } = useRoles();

  const { userId } = useAuth();
  const { t } = useLocalization();
  const { closeModal } = useModal();

  const USER_SELECTION_CONFIG: Record<
    SelectableUserRole,
    { endpoint: string; role: string; roleTypes?: readonly UserRole[] }
  > = {
    [USER_ROLES.PLAYER]: {
      endpoint: 'customer/search',
      role: USER_ROLES.AGENT,
    },
    [USER_ROLES.AGENT]: {
      endpoint: 'agent/search',
      role: USER_ROLES.MANAGER,
      roleTypes: [USER_ROLES.AGENT],
    },
    [USER_ROLES.MANAGER]: {
      endpoint: 'agent/search',
      role: 'Administrator',
      roleTypes: [USER_ROLES.MANAGER],
    },
  } as const;

  const config = USER_SELECTION_CONFIG[userType];

  const {
    items: users = [],
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    lastElementRef,
  } = useInfiniteScroll<Customer>({
    queryKey: [QUERY_KEYS.customerSearch, config.endpoint, userId, debouncedSearchQuery, userType, config.roleTypes],
    endpoint: config.endpoint,
    queryParams: {
      limit: 25,
      search: debouncedSearchQuery || undefined,
      roleTypes: config.roleTypes,
    },
    apiService: 'crm',
    customHeaders: {
      role: config.role,
      agentId: userId,
    },
    enabled: !!userId && !isPlayer,
  });

  const formattedUsers = useMemo(
    () =>
      users.map((customer: Customer) => ({
        id: customer.userId,
        username: customer.username || customer.userName || '',
        isActive: customer.isActive,
      })),
    [users]
  );

  const handleUserSelect = (user: SelectedUser | null) => {
    onUserSelect(user);
    closeModal();
  };

  const handleClose = () => {
    setSearchQuery('');
    closeModal();
  };

  const showEmptyState = !isLoading && formattedUsers.length === 0;

  return (
    <Box sx={styles.modalContent}>
      <Box sx={styles.header}>
        <Typography
          variant='h4'
          sx={{
            color: 'neutral.800',
          }}
        >
          {title}
        </Typography>
        <IconButton onClick={handleClose} size='small' aria-label={t('close')}>
          <Close />
        </IconButton>
      </Box>

      <TextField
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={t('search')}
        variant='standard'
        sx={styles.searchTextField}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position='end'>
                <IconButton onClick={() => setSearchQuery('')} edge='end' size='small'>
                  <Close fontSize='small' />
                </IconButton>
              </InputAdornment>
            ),
            sx: styles.searchInput,
          },
        }}
      />

      <List sx={styles.userList}>
        {isLoading && !isPlayer ? (
          <LoaderContainer size={24} />
        ) : showEmptyState ? (
          <Box sx={styles.emptyState}>
            <Typography
              variant='body2'
              sx={{
                color: 'neutral.400',
              }}
            >
              {debouncedSearchQuery ? t('noUsersFound') : t('noUsersAvailable')}
            </Typography>
          </Box>
        ) : (
          <>
            {formattedUsers.map((user) => {
              const isSelected = selectedUserId === user.id;

              return (
                <ListItem key={user.id} disablePadding ref={lastElementRef}>
                  <ListItemButton
                    onClick={() => handleUserSelect(isSelected ? null : user)}
                    sx={mergeSx(styles.userButton, isSelected ? styles.selectedUserButton : undefined)}
                    disabled={!user.isActive}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'neutral.800',
                      }}
                    >
                      {user.username}
                    </Typography>
                    {isSelected && <Check sx={styles.checkIcon} />}
                  </ListItemButton>
                </ListItem>
              );
            })}
            {isFetchingNextPage && (
              <InfiniteScrollLoader
                isLoading={isLoading}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                lastElementRef={lastElementRef}
                noEndPadding
              />
            )}
          </>
        )}
      </List>
    </Box>
  );
};

export default UserSelectionModal;
